import { memo } from 'react';
import { FormattedMessage } from 'react-intl';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';

import { messages } from './messages';
import { COST_REVIEW_TIMEOUT_ERROR } from '../../../../store/bulk/bulk-error-types';
import FailedCostReviewBatchNotification from '../../../batches/failed-batch-notifications/failed-cost-review-notification';
import { CostReviewLabelProviderSection } from '../../../cost-review-line-item/cost-review-label-provider-section';
import CostReviewLineItemInsurance from '../../../cost-review-line-item/cost-review-line-item-insurance';
import { WalletCostReviewSection } from '../../../walleted-cost-review-line-item/wallet-cost-review-section';

/**
 * @typedef {import('../../../../prop-types/cost-review.types').CostReviewWarning} CostReviewWarning
 * @typedef {import('../../../../prop-types/cost-review.types').InsuranceProviderCostReview} InsuranceProviderCostReview
 * @typedef {import('../../../../prop-types/cost-review.types').LabelProviderCostReview} LabelProviderCostReview
 * @typedef {import('../../../../prop-types/cost-review.types').WalletedProviderCostReview} WalletedProviderCostReview
 * @typedef {import('../../../../prop-types/cost-review.types').CostReviewShippingServiceRecommendationMap} CostReviewShippingServiceRecommendationMap
 * @typedef {import('../../../../prop-types/cost-review.types').CostReviewSelectedRecommendations} CostReviewSelectedRecommendations
 * @typedef {import('../../../../prop-types/cost-review.types').CostReviewInsuranceRecommendationMap} CostReviewInsuranceRecommendationMap
 * @typedef {import('../../../../prop-types/cost-review.types').CostReviewRateShopperRecommendationMap} CostReviewRateShopperRecommendationMap
 */

/**
 * @param {object} props
 * @param {() => void} props.closeOrderDetailsModal
 * @param {Array<CostReviewWarning>} props.costWarnings
 * @param {InsuranceProviderCostReview[]} props.insurances
 * @param {(provider: string, amount: number) => void} props.insurancePurchaseAmountChanged
 * @param {boolean} props.localDeliveryShipmentIsPresent
 * @param {LabelProviderCostReview[]} props.providers
 * @param {(provider: string, amount: number) => void} props.providerPurchaseAmountChanged
 * @param {number} props.shipFromCount
 * @param {boolean} props.userHasBillingInfoSetup
 * @param {(walletId: string, amount: number) => void} props.walletPurchaseAmountChanged
 * @param {WalletedProviderCostReview[][]} props.walletedProviderGroups
 * @param {string} props.error
 * @param {boolean} props.shouldShowError
 * @param {() => void} props.onRetry
 * @param {CostReviewShippingServiceRecommendationMap} props.shippingServiceRecommendations
 * @param {CostReviewSelectedRecommendations} props.selectedShippingServiceRecommendations
 * @param {(recommendationKey: string) => void} props.toggleShippingServiceRecommendation
 * @param {CostReviewSelectedRecommendations} props.selectedInsuranceRecommendations
 * @param {CostReviewInsuranceRecommendationMap} props.insuranceRecommendations
 * @param {(recommendationKey: string) => void} props.toggleInsuranceRecommendation
 * @param {boolean} props.isToggleRecommendationDisabled
 * @param {CostReviewRateShopperRecommendationMap} [props.rateShopperRecommendations]
 * @param {CostReviewSelectedRecommendations} [props.selectedRateShopperRecommendations]
 * @param {(recommendationKey: string) => void} [props.onToggleRateShopperRecommendation]
 * @returns {React.JSX.Element}
 */
const CostReviewModalBodyContent = ({
  closeOrderDetailsModal,
  costWarnings,
  insurances,
  insurancePurchaseAmountChanged,
  localDeliveryShipmentIsPresent,
  providers,
  providerPurchaseAmountChanged,
  shipFromCount,
  userHasBillingInfoSetup,
  walletPurchaseAmountChanged,
  walletedProviderGroups,
  error,
  shouldShowError,
  onRetry,
  shippingServiceRecommendations,
  selectedShippingServiceRecommendations,
  toggleShippingServiceRecommendation,
  selectedInsuranceRecommendations,
  insuranceRecommendations,
  toggleInsuranceRecommendation,
  isToggleRecommendationDisabled,
  rateShopperRecommendations,
  selectedRateShopperRecommendations,
  onToggleRateShopperRecommendation,
}) => {
  if (shouldShowError && error === COST_REVIEW_TIMEOUT_ERROR) {
    return <FailedCostReviewBatchNotification onRetry={onRetry} />;
  }

  return (
    <>
      {walletedProviderGroups.map((walletProviderGroup) => (
        <WalletCostReviewSection
          key={walletProviderGroup[0].walletId}
          insuranceRecommendations={insuranceRecommendations}
          isToggleRecommendationDisabled={isToggleRecommendationDisabled}
          onPurchaseAmountChange={walletPurchaseAmountChanged}
          onToggleRateShopperRecommendation={onToggleRateShopperRecommendation}
          onToggleShippingServiceRecommendation={toggleShippingServiceRecommendation}
          rateShopperRecommendations={rateShopperRecommendations}
          selectedInsuranceRecommendations={selectedInsuranceRecommendations}
          selectedRateShopperRecommendations={selectedRateShopperRecommendations}
          selectedShippingServiceRecommendations={selectedShippingServiceRecommendations}
          shippingServiceRecommendations={shippingServiceRecommendations}
          toggleInsuranceRecommendation={toggleInsuranceRecommendation}
          walletProviderGroup={walletProviderGroup}
        />
      ))}
      {providers.map((provider, index) => {
        const warningForEachLineItem = costWarnings.filter((warning) => {
          return warning.sellerLabelProviderId === provider.sellerLabelProviderId;
        });

        const insuranceRecommendation = insuranceRecommendations[provider.sellerLabelProviderId];
        const isInsuranceRecommendationSelected = Boolean(selectedInsuranceRecommendations[insuranceRecommendation?.key]);

        return (
          <CostReviewLabelProviderSection
            key={index}
            costReviewLineItemProvider={provider}
            costWarnings={warningForEachLineItem}
            insuranceRecommendation={insuranceRecommendation}
            isInsuranceRecommendationSelected={isInsuranceRecommendationSelected}
            isToggleRecommendationDisabled={isToggleRecommendationDisabled}
            onPurchaseAmountChange={providerPurchaseAmountChanged}
            onToggleRateShopperRecommendation={onToggleRateShopperRecommendation}
            onToggleShippingServiceRecommendation={toggleShippingServiceRecommendation}
            rateShopperRecommendations={rateShopperRecommendations[provider.sellerLabelProviderId]}
            selectedRateShopperRecommendations={selectedRateShopperRecommendations}
            selectedShippingServiceRecommendations={selectedShippingServiceRecommendations}
            shippingServiceRecommendations={shippingServiceRecommendations[provider.sellerLabelProviderId]}
            toggleInsuranceRecommendation={toggleInsuranceRecommendation}
          />
        );
      })}
      {insurances.map((insurance, index) => (
        <CostReviewLineItemInsurance
          key={index}
          closeOrderDetailsModal={closeOrderDetailsModal}
          insuranceProvider={insurance}
          onPurchaseAmountChange={insurancePurchaseAmountChanged}
          userHasBillingInfoSetup={userHasBillingInfoSetup}
        />
      ))}
      {localDeliveryShipmentIsPresent ? (
        <BannerMessageV2 content={<FormattedMessage {...messages.localDeliveryShipDateNotAppliedInfo} />} status={BannerStatus.Info} />
      ) : null}
      {shipFromCount > 1 && (
        <BannerMessageV2
          content={<FormattedMessage {...messages.autoBatchSplittingWarning} values={{ count: shipFromCount }} />}
          status={BannerStatus.Warning}
        />
      )}
    </>
  );
};

const memoizedCostReviewModalBodyContent = memo(CostReviewModalBodyContent);

export { memoizedCostReviewModalBodyContent as CostReviewModalBodyContent };
